import { AppName, EncounterStatus } from '@hb/shared'

// §1.6 / Addendum M.2: which surface an open encounter renders on.
// 'view' is the read-only EncounterView; 'form' is EncounterForm.
export type EncounterSurface = 'form' | 'view'

// Why the provider is currently in the form for an already-saved note:
// 'editing' — free edit of a Draft/Saved note (M.2: no unlock step, no reason required)
// 'amending' — an authenticated note reopened via the Amend flow (§1.6.1)
export type EncounterOpenMode = 'editing' | 'amending'

// M.2: Draft (or brand-new) notes open straight into the form. Saved/Authenticated
// notes open read-only first; the provider switches to the form via onEdit/Amend.
// Unknown status falls back to 'editing' so the note opens in the form (the
// pre-existing providers-app behavior) rather than trapping a draft in the view.
export const getInitialOpenMode = (
  examId: string | null,
  status?: EncounterStatus,
): EncounterOpenMode | null => {
  if (!examId) return 'editing'
  if (!status || status === EncounterStatus.DRAFT) return 'editing'
  return null
}

// Decisions 2 & 3 (2026-07-07): amendments (§1.6.1) and quick-auth (§1.6) are
// provider-only. The admin app never gets an onEdit callback — so admins see no Amend
// button, and COMPLETED encounters render the full read-only note (which billing uses
// for internal review, §6.4.1) instead of the quick-auth summary. Server-side
// enforcement lives in functions-base buildEncounterDoc.
export const getCanEditEncounter = (appName: AppName): boolean => appName !== 'app'

// Status-driven routing (§1.6, Addendum M.2, decisions 2026-07-07):
// - Admin app ('app'): unchanged — existing encounters always open in the read-only
//   view, new encounters in the form. (Admin-side gating changes are work item 2.)
// - Providers app: AUTHENTICATED → read-only view unless mid-amendment; COMPLETED →
//   view (quick-auth summary) unless the provider tapped through to the form;
//   DRAFT/new/unknown → form. Because AUTHENTICATED only stays in the form while
//   'amending', a provider who authenticates from inside the form lands on the
//   read-only view as soon as the status flips.
export const getEncounterSurface = ({
  appName,
  examId,
  status,
  mode,
}: {
  appName: AppName
  examId: string | null
  status?: EncounterStatus
  mode: EncounterOpenMode | null
}): EncounterSurface => {
  if (appName === 'app') return examId ? 'view' : 'form'
  if (!examId) return 'form'
  if (status === EncounterStatus.AUTHENTICATED) return mode === 'amending' ? 'form' : 'view'
  if (status === EncounterStatus.COMPLETED) return mode ? 'form' : 'view'
  return 'form'
}
